import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle, Camera, Check, Droplets, Flame, Lock, Loader2, Pencil, Trash2, Upload, X, Zap,
} from "lucide-react";
import {
  CUR_KEY, TODAY, UNIT,
  billingMonthFor, consumptionForMonth, daysBetween, fmtQty, heDate, itemDue, itemPaid,
  monthLabel, nis, pad, readingBefore, readingSanity, readingsOf, rowKey,
} from "@rental/shared";
import { C, METER_HUES, inputStyle, readingInputStyle } from "./theme.js";
import { ApiError, ai, uploadImage } from "./lib/api.js";
import { compressForUpload } from "./lib/images.js";
import { DateField, FilePick, StoredImage } from "./ui.jsx";
import { openLightbox } from "./ZoomLightbox.jsx";

/* billed — האם הסעיף נגבה דרך בעל הנכס. כשהדייר משלם ישירות לספק הקריאות
   נשמרות לתיעוד, אבל לא מוצג שום סכום בשקלים — אין כאן חשבון.

   עבר לקובץ עצמאי (1.9.2026) כדי ש-App.jsx ו-CollectionBoards.jsx
   ישתמשו באותה קומפוננטה בדיוק — בלי import מעגלי בין שני קבצי web. */
export function MeterBlock({ apt, meter, data, rate, onAdd, onEdit, onDelete, month = CUR_KEY, billed = true }) {
  /* המסך מציג חודש נבחר, והבלוק הזה הציג תמיד את הקריאות האחרונות בכלל
     ותאריך "היום" — בתצוגת מרץ ראו את הקריאה של אוגוסט. כאן הכול נחתך
     לחודש המוצג: הקריאות שעד סופו, והתאריך לקריאה חדשה בתוכו. */
  const isCur = month === CUR_KEY;
  const monthEnd = `${month}-${pad(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate())}`;
  const defaultDate = isCur ? TODAY : monthEnd;
  const [val, setVal] = useState("");
  const [date, setDate] = useState(defaultDate);
  // מעבר חודש במסך — התאריך המוצע עובר איתו, כל עוד לא נגעו בו.
  const touchedDate = useRef(false);
  useEffect(() => { if (!touchedDate.current) setDate(defaultDate); }, [defaultDate]);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [photoId, setPhotoId] = useState(null);
  const [scanNote, setScanNote] = useState("");
  const [jump, setJump] = useState(null);   // קריאה חריגה שממתינה לאישור

  const all = readingsOf(data, apt.id, meter);
  const list = all.filter((r) => r.date <= monthEnd);
  const last = list[list.length - 1] || null;
  const prev = last ? readingBefore(data, apt.id, meter, last.date, last.id) : null;
  // קריאות מאוחרות יותר קיימות אבל שייכות לחודש אחר — נאמר את זה במפורש.
  const laterCount = all.length - list.length;
  const diff = last && prev ? last.value - prev.value : null;
  /* קריאה שנשמרה ונראית כמו ספרה שנקראה לא נכון (3452 במקום 345.2) לא
     קיבלה שום סימון — רק הסכום המופרך שחושב ממנה. אותה בדיקה שרצה
     בהקלדה רצה גם על האחרונה שנשמרה. */
  const lastCheck = last && prev
    ? readingSanity({ ...data, readings: (data.readings || []).filter((r) => r.id !== last.id) }, apt.id, meter, last.value, last.date)
    : null;
  const suspicious = lastCheck?.code === "jump";
  const Icon = meter === "elec" ? Zap : meter === "gas" ? Flame : Droplets;
  const METER_LABEL = { elec: "מונה חשמל", water: "מונה מים", gas: "מונה גז" };

  const [okMsg, setOkMsg] = useState("");
  const add = (confirmed = false) => {
    const n = Number(val);
    const check = readingSanity(data, apt.id, meter, val, date);

    if (check.code === "not_a_number") { setErr("צריך להזין מספר"); return; }
    if (check.code === "future_date") { setErr("התאריך עתידי — בחרו תאריך שכבר עבר"); return; }
    if (check.code === "below_previous") {
      setErr(`הקריאה נמוכה מהקודמת (${check.previous}) — בדקו את המספר`); return;
    }
    // קפיצה חריגה היא בדרך כלל ספרה שנקראה לא נכון. לא חוסמים, אבל לא
    // נותנים לזה להיכנס בלי שראו את המספר פעם שנייה.
    if (check.code === "jump" && !confirmed) {
      setErr("");
      setJump(`${Math.round(check.diff)} ${UNIT[meter]} ב-${check.days} ימים — הרבה מעל הרגיל כאן. הקריאה נכונה?`);
      return;
    }

    setErr(""); setJump(null);
    // ההפרש מגיע מהבדיקה ולא מהקריאה האחרונה ברשימה — כך גם קריאה
    // שנרשמת בדיעבד מדווחת על הצריכה שלה ולא על זו של המאוחרת ממנה.
    const d2 = check.diff ?? null;
    onAdd(apt.id, meter, n, date, photoId);
    setVal(""); setPhotoId(null); setScanNote("");
    setOkMsg(d2 > 0
      ? `נקלט. צריכה ${d2} ${UNIT[meter]}` + (billed ? ` · חויב ${nis(d2 * rate)}` : " · לתיעוד — משולם ישירות לספק")
      : "הקריאה נשמרה");
    setTimeout(() => setOkMsg(""), 4000);
  };

  /* צילום המונה: המספר נקרא בשרת ונכנס לשדה, ולא נשמר לבד. בעל הנכס
     רואה אותו מול התמונה ומאשר — קריאה שגויה כאן היא חשבון שגוי לדייר. */
  const scan = async (file) => {
    if (!file) return;
    setBusy(true); setErr(""); setScanNote(""); setJump(null);
    try {
      const small = await compressForUpload(file);
      let id = null;
      // התמונה היא התיעוד — כישלון העלאה לא מבטל את הקריאה עצמה.
      try { id = await uploadImage(small, { scope: "owner", kind: "meter", apartmentId: apt.id }); }
      catch { id = null; }
      setPhotoId(id);

      const out = await ai.readMeter(meter, small, { scope: "owner" });
      if (out.reading == null) {
        setScanNote(out.note || "לא הצלחתי לקרוא את המונה מהתמונה. הקלידו את המספר.");
      } else {
        setVal(String(out.reading));
        setScanNote(out.confidence === "low"
          ? `זיהיתי ${out.reading}, אבל לא בוודאות. ודאו מול התמונה לפני ההוספה.`
          : `זיהיתי ${out.reading}. בדקו ולחצו הוספה.`);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "קריאת התמונה נכשלה. אפשר להקליד ידנית.");
    }
    setBusy(false);
  };

  const saveEdit = (r) => {
    const n = Number(editVal);
    if (Number.isNaN(n)) return;
    onEdit(r.id, { value: n }); setEditId(null);
  };

  /* קריאה שנכללה בגבייה נעולה לתיקון ולמחיקה — שינוי שלה מחשב מחדש
     חשבון שהדייר כבר שילם. נקודת "שולם עד" (paidThrough, נרשמת ברגע
     סימון התשלום) מפרידה: מה שקדם לה נעול, קריאה שנוספה אחרי התשלום
     פתוחה לתיקון עד שגם היתרה שלה תיגבה. חשבון שנגבה לפני שהנקודה
     הייתה קיימת — כל קריאות החודש נעולות (שמרני). ביטול סימון התשלום
     פותח הכול. קריאת מים נועלת גם דרך הביוב — שניהם נגזרים ממנה. */
  const collectedAt = (r) => {
    if (!billed) return false;
    const bl = data.bills[rowKey(apt.id, billingMonthFor(apt, data.settings || {}, r.date))];
    if (!bl) return false;
    return (meter === "water" ? ["water", "sewage"] : [meter]).some((k) => {
      const it = bl[k];
      if (!(itemPaid(it) > 0)) return false;
      return it.paidThrough
        ? r.date <= it.paidThrough.date && Number(r.value) <= Number(it.paidThrough.value)
        : true;
    });
  };

  return (
    <div className={`em-meter ${meter} rounded-lg border p-3`} style={{ borderColor: C.line, background: C.card }}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 text-sm" style={{ fontWeight: 700 }}>
          <span className="rounded-lg flex items-center justify-center shrink-0"
            style={{ width: 30, height: 30, background: METER_HUES[meter].bg, color: METER_HUES[meter].main }}>
            <Icon size={15} />
          </span>
          {METER_LABEL[meter]}
          {!billed && <span className="text-[10px]" style={{ color: C.muted, fontWeight: 500 }}>· משולם ישירות לספק</span>}
        </div>
        {last && <div className="text-[11px]" style={{ color: C.muted }}>אחרונה: {last.value} ({heDate(last.date)})</div>}
      </div>

      {/* החשבון של החודש — בתוך כרטיס המונה, במקום קוביה נפרדת ברשת:
          סכום, מה שולם ומה נותר, בצבעי הלוחות (ירוק שולם / אדום פתוח).
          ביוב נכלל במים. את התשלום מסמנים בלוח הצריכה שבסקירה. */}
      {billed && (() => {
        const bl = data.bills[rowKey(apt.id, month)] || {};
        const items = (meter === "water" ? [bl.water, bl.sewage] : [bl[meter]]).filter(Boolean);
        const amt = items.reduce((t, it) => t + Number(it?.amount || 0), 0);
        const paidAmt = items.reduce((t, it) => t + itemPaid(it), 0);
        const due = Math.max(0, amt - paidAmt);
        if (!(amt > 0 || paidAmt > 0)) return null;
        const paidFull = amt > 0 && due <= 0;
        return (
          <div className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 mb-2 text-[12px]"
            title="את התשלום מסמנים בלוח חשבונות הצריכה שבסקירה"
            style={paidFull ? { background: "#EAF3EE", color: C.paid } : { background: "#FBEDED", color: C.late }}>
            <span>חשבון {monthLabel(month)}{meter === "water" && Number(bl.sewage?.amount || 0) > 0 ? " (כולל ביוב)" : ""}</span>
            <b style={{ fontVariantNumeric: "tabular-nums" }}>
              {nis(amt)}{paidFull
                ? (paidAmt > amt ? ` · שולם (עודף ${nis(paidAmt - amt)})` : " · שולם")
                : paidAmt > 0 ? ` · נותרו ${nis(due)}` : " · לתשלום"}
            </b>
          </div>
        );
      })()}

      {/* החשבון של החודש מבוסס על צריכת תקופת החיוב — לא רק על ההפרש
          מהקריאה האחרונה (שעלול להיות יום אחד). כששני המספרים שונים,
          שניהם מוצגים כדי שהמספר בכרטיס החשבון יסתדר עם מה שרואים כאן. */}
      {(() => {
        const cons = consumptionForMonth(data, apt, data.settings || {}, meter, month);
        if (!cons || !(cons.diff > 0) || !cons.from || !cons.to) return null;
        if (diff != null && Math.abs(cons.diff - diff) < 0.001) return null;
        return (
          <div className="text-[11px] mb-1" style={{ color: C.ink }}>
            החודש: {fmtQty(cons.diff)} {UNIT[meter]} (מ-{cons.from.value} ל-{cons.to.value}){billed ? ` ≈ ${nis(cons.diff * rate)}` : ""}
          </div>
        );
      })()}
      {/* חלק ששולם מול קריאה שנוספה אחריו: בלי השורה הזו רואים רק סכום
          חודש שהשתנה ו"נותרו" על החשבון, בלי מה שיצר את היתרה. */}
      {billed && (() => {
        const it = data.bills[rowKey(apt.id, month)]?.[meter];
        const pt = it?.paidThrough;
        if (!pt || !(itemPaid(it) > 0) || itemDue(it) <= 0) return null;
        const extra = last ? Number(last.value) - Number(pt.value) : 0;
        if (!(extra > 0)) return null;
        return (
          <div className="text-[11px] mb-2 rounded-lg px-2 py-1.5" style={{ background: "#FBF7EE", color: "#6B4E12" }}>
            שולם עד קריאה {pt.value} ({heDate(pt.date)}) · קריאות שנוספו מאז: עוד {fmtQty(extra)} {UNIT[meter]} ≈ {nis(extra * rate)} — מופיע כיתרה לגבייה.
          </div>
        );
      })()}
      {diff != null ? (
        <div className="text-[11px] mb-2" style={{ color: suspicious ? C.late : diff > 0 ? C.ink : C.muted }}>
          מאז הקריאה הקודמת: {fmtQty(diff)} {UNIT[meter]} מ-{heDate(prev.date)} ({daysBetween(prev.date, last.date)} ימים){billed ? ` ≈ ${nis(diff * rate)}` : ""}
          {suspicious && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 mr-1" style={{ background: "#FBEAE6", color: C.late }}>
              <AlertTriangle size={10} /> קפיצה חריגה — לבדוק את הקריאה
            </span>
          )}
        </div>
      ) : (
        <div className="text-[11px] mb-2" style={{ color: C.muted }}>
          {last ? "זו הקריאה הראשונה — הצריכה תחושב מהקריאה הבאה" : isCur ? "אין עדיין קריאות" : `אין קריאות עד ${monthLabel(month)}`}
        </div>
      )}
      {laterCount > 0 && (
        <div className="text-[10px] mb-2" style={{ color: C.ochre }}>
          יש עוד {laterCount} {laterCount === 1 ? "קריאה" : "קריאות"} אחרי {monthLabel(month)} — מוצגות בחודש שלהן.
        </div>
      )}

      {/* הקריאה בשורה משלה. קודם היא חלקה שורה אחת עם תאריך ושלושה
          כפתורים, ובטלפון נשארו לה כמה עשרות פיקסלים — פחות מהמספר. */}
      <input type="number" inputMode="numeric" value={val} aria-label={`קריאת ${METER_LABEL[meter]}`}
        onChange={(e) => { setVal(e.target.value); setJump(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") add(); }}
        placeholder="קריאה חדשה" style={readingInputStyle} />

      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {/* min-w: בטלפון התאריך נחתך ל-"22/08/202" כשנדחק בין הכפתורים;
            עם flex-wrap ורוחב מינימלי הוא יורד לשורה משלו במקום להיחתך. */}
        <div className="flex-1 min-w-[150px]">
          <DateField value={date} onChange={(v) => { touchedDate.current = true; setDate(v); }} ariaLabel="תאריך הקריאה" style={{ padding: "8px 10px", fontSize: 13 }} />
        </div>
        <FilePick capture title="צילום המונה" disabled={busy} onPick={scan}
          className="rounded-lg px-2.5 shrink-0 flex items-center" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
        </FilePick>
        <FilePick title="בחירת תמונה מהגלריה" disabled={busy} onPick={scan}
          className="rounded-lg px-2.5 shrink-0 flex items-center" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
          <Upload size={15} />
        </FilePick>
        <button onClick={() => add()} className="rounded-lg px-4 text-sm shrink-0" style={{ background: C.teal, color: "#fff" }}>הוספה</button>
      </div>

      {photoId && (
        <div className="flex items-center gap-2 mt-1.5 text-[11px]" style={{ color: C.muted }}>
          <button type="button" onClick={() => openLightbox(photoId, "owner")} aria-label="הגדלת צילום המונה" title="לחיצה להגדלה">
            <StoredImage id={photoId} className="rounded" style={{ height: 36 }} />
          </button>
          התמונה תישמר יחד עם הקריאה
        </div>
      )}
      {scanNote && <div className="text-[11px] mt-1" style={{ color: C.ink }}>{scanNote}</div>}
      {jump && (
        <div className="mt-1.5 rounded-lg p-2 text-[11px] flex items-center justify-between gap-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <span>{jump}</span>
          <button onClick={() => add(true)} className="rounded-md px-2 py-1 shrink-0" style={{ background: C.ochre, color: "#fff" }}>כן, להוסיף</button>
        </div>
      )}
      {err && <div className="text-[11px] mt-1" style={{ color: C.late }}>{err}</div>}
      {okMsg && <div className="text-[11px] mt-1" style={{ color: C.paid }}>{okMsg}</div>}

      {list.length > 0 && (
        <div className="mt-2 divide-y" style={{ borderColor: C.line }}>
          {[...list].reverse().slice(0, 5).map((r) => (
            <div key={r.id} className="flex items-center justify-between py-1.5 text-[12px]">
              <div className="flex items-center gap-2">
                <span style={{ color: C.muted }}>{heDate(r.date)}</span>
                {editId === r.id ? (
                  <input type="number" inputMode="numeric" value={editVal} autoFocus aria-label="תיקון הקריאה"
                    onChange={(e) => setEditVal(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit(r)}
                    style={{ ...inputStyle, padding: "3px 8px", fontSize: 14, width: 120, fontVariantNumeric: "tabular-nums" }} />
                ) : <span style={{ fontWeight: 600 }}>{r.value}</span>}
                {r.note && <span className="text-[10px]" style={{ color: C.ochre }}>{r.note}</span>}
                {r.photoId && (
                  <button type="button" onClick={() => openLightbox(r.photoId, "owner")} aria-label="הצגת צילום המונה" title="לחיצה להגדלת הצילום"
                    className="flex items-center gap-0.5" style={{ color: C.teal }}>
                    <Camera size={11} /><span className="text-[10px]">צילום</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {editId === r.id ? (
                  <>
                    <button onClick={() => saveEdit(r)} style={{ color: C.paid }} aria-label="שמירה"><Check size={13} /></button>
                    <button onClick={() => setEditId(null)} style={{ color: C.muted }} aria-label="ביטול"><X size={13} /></button>
                  </>
                ) : collectedAt(r) ? (
                  <span title="הקריאה חויבה בחשבון שכבר נגבה — נעולה לעריכה. ביטול סימון התשלום פותח אותה."
                    aria-label="נעולה לעריכה" style={{ color: C.muted }}>
                    <Lock size={12} />
                  </span>
                ) : (
                  <>
                    <button onClick={() => { setEditId(r.id); setEditVal(String(r.value)); }} style={{ color: C.muted }} aria-label="תיקון"><Pencil size={12} /></button>
                    <button onClick={() => onDelete(r.id)} style={{ color: C.muted }} aria-label="מחיקה"><Trash2 size={12} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
